import { initializeCardsCarouselRoot } from '@fc/cards-shell';
import { createReviewLoop } from '@fc/review-loop';

/** Only an unambiguous product card in this review can supply its CTA URL. */
export function synchronizeReviewProductButtons(card) {
  if (card.closest('[data-fit-review-loop-copy]')) return;
  const owned = (selector) => [...card.querySelectorAll(selector)]
    .filter((element) => element.closest('.fc-review-card--container') === card);
  const products = owned('[data-fit-product-card-url]');
  const url = products.length === 1 ? products[0].dataset.fitProductCardUrl : '';
  for (const button of owned('[data-fit-review-product-cta]')) {
    if (url) {
      button.setAttribute('href', url);
      button.removeAttribute('aria-disabled');
      button.removeAttribute('role');
      button.hidden = false;
    } else {
      button.removeAttribute('href');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('role', 'link');
      button.hidden = !button.hasAttribute('data-fit-review-cta-editor');
    }
  }
}

/** Group existing review blocks without replacing their editor or media nodes. */
export function initializeReviewPanels(root = document) {
  const cards = [
    ...(root instanceof Element && root.matches('.fc-review-card--container') ? [root] : []),
    ...root.querySelectorAll('.fc-review-card--container'),
  ];

  for (const card of cards) {
    synchronizeReviewProductButtons(card);
    // Keep the shell's first/last-child spacing contract after loading.
    card.querySelector(':scope > [data-fit-review-panel-loader]')?.remove();
    if (card.dataset.reviewPanelsReady === 'true') continue;
    const children = [...card.children];
    if (!children.some((child) => child.matches('.fit-video-wrapper'))) continue;

    let panel = null;
    for (const child of children) {
      if (child.matches('style, script, template, .fc-content-card__link')) continue;
      if (child.matches('.fit-video-wrapper, .fc-content-card__media')) {
        panel = null;
        continue;
      }
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'fc-content-card__body fc-review-card__panel';
        card.insertBefore(panel, child);
      }
      panel.append(child);
    }
    card.dataset.reviewPanelsReady = 'true';
  }
}

const reviewCarouselSelector = '[data-fit-review-carousel]';
const reviewCarousels = new WeakMap();

function reviewRoots(root) {
  return [
    ...(root instanceof Element && root.matches(reviewCarouselSelector) ? [root] : []),
    ...root.querySelectorAll(reviewCarouselSelector),
  ];
}

export function initializeReviewCarousels(root = document) {
  initializeReviewPanels(root);
  for (const carousel of reviewRoots(root)) {
    if (reviewCarousels.has(carousel)) continue;
    initializeCardsCarouselRoot(carousel);
    const scroller = carousel.querySelector('carousel-slides');
    if (!scroller || !carousel.hasAttribute('data-fit-video-review-showcase')) continue;

    const controller = new AbortController();
    const slides = [...scroller.querySelectorAll(':scope > carousel-slide')];
    const visibleSlides = () => slides.filter((slide) => !slide.hidden);
    const { signal } = controller;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const nativeVideo = (player) => player?.querySelector('video') || player?.querySelector('template')?.content.querySelector('video');
    const players = [...scroller.querySelectorAll('lazy-media')].filter((player) => nativeVideo(player) instanceof HTMLVideoElement);
    const visiblePlayer = (slide) => players.find((player) => slide?.contains(player) && player.getClientRects().length);
    const inHorizontalView = (player) => {
      const bounds = player.getBoundingClientRect();
      const viewport = scroller.getBoundingClientRect();
      return bounds.left + bounds.width > viewport.left && bounds.left < viewport.left + viewport.width;
    };
    const controlsTemplate = carousel.querySelector('[data-fit-review-controls-template]');
    const controls = new Map();
    const nativeControls = new Map();
    const systemPauses = new WeakSet();
    const originalMinHeight = scroller.style.minHeight;
    let frame = 0;
    let settleTimer = 0;
    let active = null;
    let allowedPlayer = null;
    let pendingSelection = null;
    let visible = false;
    let previewRatio = 0;
    let ready = false;
    let userPaused = false;
    let muted = true;
    let explicitPlayback = false;
    let userScrolling = false;
    let suspended = false;
    let loop = null;
    const fresh = Math.abs(scroller.scrollLeft) < 2;

    const pause = (player) => {
      const video = player.querySelector('video');
      if (video && !video.paused) systemPauses.add(video);
      player.pauseMedia?.();
    };
    const pauseOthers = (except = null) => {
      for (const player of players) if (player !== except) pause(player);
    };
    const syncControls = () => {
      for (const [player, toolbar] of controls) {
        const video = player.querySelector('video');
        toolbar.hidden = !active?.contains(player) || !video;
        const playing = video && !video.paused && !video.ended;
        const toggle = toolbar.querySelector('[data-fit-review-play]');
        toggle.setAttribute('aria-label', playing ? toolbar.dataset.pauseLabel : toolbar.dataset.playLabel);
        toolbar.querySelector('[data-play-icon]').hidden = playing;
        toolbar.querySelector('[data-pause-icon]').hidden = !playing;
        const isMuted = video?.muted ?? muted;
        toolbar.querySelector('[data-fit-review-sound]').setAttribute('aria-label', isMuted ? toolbar.dataset.unmuteLabel : toolbar.dataset.muteLabel);
        toolbar.querySelector('[data-muted-icon]').hidden = !isMuted;
        toolbar.querySelector('[data-sound-icon]').hidden = isMuted;
      }
    };
    const load = (player) => {
      const video = nativeVideo(player);
      // The section owns native autoplay; lazy-media still owns loading and controls.
      video.removeAttribute('autoplay');
      video.defaultMuted = true;
      video.muted = muted;
      if (nativeControls.has(player)) video.controls = false;
      player.loadContent(false);
      player.querySelector('video').muted = muted;
      if (nativeControls.has(player)) player.querySelector('video').controls = false;
    };
    const playSelected = (explicit = false) => {
      const player = visiblePlayer(active);
      if (!ready || !player || !visible || !inHorizontalView(player) || document.hidden || suspended || userPaused) return;
      if (!explicit && (!player.hasAttribute('autoplay') || reducedMotion.matches || previewRatio < 0.5)) return;
      allowedPlayer = player;
      pauseOthers(player);
      load(player);
      player.playMedia();
      syncControls();
    };
    const centerSlide = (slide, behavior = reducedMotion.matches ? 'instant' : 'smooth') => {
      const bounds = slide.getBoundingClientRect();
      const viewport = scroller.getBoundingClientRect();
      const scale = viewport.width / scroller.clientWidth || 1;
      const offset = (bounds.left + bounds.width / 2 - viewport.left - viewport.width / 2) / scale;
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const rtl = getComputedStyle(scroller).direction === 'rtl';
      const target = Math.max(rtl ? -maxScroll : 0, Math.min(rtl ? 0 : maxScroll, scroller.scrollLeft + offset));
      const left = target - scroller.scrollLeft;
      // Fractional card widths can leave a subpixel offset. Scrolling that tiny
      // distance with mandatory snap can unexpectedly select the adjacent card.
      if (Math.abs(left) < 1) return;
      scroller.scrollBy({ left, behavior });
    };
    const sizeFrames = () => {
      const desktop = matchMedia('(min-width: 750px)').matches;
      let height = 0;
      for (const slide of visibleSlides()) {
        let extraHeight = 0;
        for (const player of players.filter((item) => slide.contains(item) && item.getClientRects().length)) {
          const wrapper = player.closest('.fit-video-wrapper');
          const mobileMedia = !desktop && wrapper.dataset.mobileSpecific === 'true';
          const automatic = wrapper.dataset[mobileMedia ? 'fitReviewRatioMobile' : 'fitReviewRatioDesktop'] === 'auto';
          if (!automatic) continue;
          const width = player.getBoundingClientRect().width;
          const expandedHeight = width * 16 / 9;
          extraHeight += expandedHeight - player.getBoundingClientRect().height;
          player.style.setProperty('--fit-review-media-height', `${slide === active ? expandedHeight : width * 4 / 3}px`);
          player.setAttribute('data-fit-review-frame', '');
        }
        height = Math.max(height, slide.getBoundingClientRect().height + extraHeight);
      }
      const style = getComputedStyle(scroller);
      scroller.style.minHeight = `${Math.ceil(height + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom))}px`;
    };
    const select = (slide) => {
      if (!slide || active === slide) return;
      active = slide;
      allowedPlayer = null;
      pauseOthers();
      for (const item of slides) {
        if (item === active) item.dataset.fitActiveReview = 'true';
        else delete item.dataset.fitActiveReview;
      }
      sizeFrames();
      syncControls();
    };
    const nearestSlide = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const distance = (slide) => {
        const bounds = slide.getBoundingClientRect();
        return Math.abs(bounds.left + bounds.width / 2 - center);
      };
      return visibleSlides().reduce((nearest, slide) => !nearest || distance(slide) < distance(nearest) - 1 ? slide : nearest, active);
    };
    const followScroll = () => {
      if (!userScrolling || pendingSelection) return;
      const next = nearestSlide();
      if (!next || next === active) return;
      select(next);
      suspended = false;
      explicitPlayback = true;
      const player = visiblePlayer(active);
      if (userPaused && player && ready && visible && !document.hidden) load(player);
      else playSelected(true);
      syncControls();
    };
    const settle = () => {
      clearTimeout(settleTimer);
      if (scroller.closest('slide-carousel')?.hasAttribute('dragging')) {
        settleTimer = setTimeout(settle, 160);
        return;
      }
      followScroll();
      // Preserve explicit selection; only a shopper scroll chooses the nearest.
      select(pendingSelection || (userScrolling ? nearestSlide() : active));
      pendingSelection = null;
      if (userScrolling) {
        suspended = false;
        explicitPlayback = true;
        // The shared drag release aligns its target to the leading edge.
        // Reviews settle the selected portrait at the viewport center instead.
        centerSlide(active);
      } else if (loop && active) {
        // Editor restoration and shared-slider setup can scroll after mounting.
        // Keep the same logical review centered after those programmatic moves.
        centerSlide(active);
      }
      userScrolling = false;
      playSelected(explicitPlayback);
    };
    const schedule = () => {
      loop?.update(pendingSelection);
      if (userScrolling && !pendingSelection && !frame) frame = requestAnimationFrame(() => {
        frame = 0;
        followScroll();
      });
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 160);
    };
    const selectVideo = (event) => {
      let target = event.target instanceof Element ? event.target : null;
      // Mostly offscreen slides remain inert for keyboard navigation. Their
      // visible video slivers can still select a review through the scroller.
      if (target === scroller && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
        const repeated = loop?.targetAt(event.clientX, event.clientY);
        if (repeated) target = visiblePlayer(repeated)?.closest('.fit-video-wrapper');
        const preview = players.find((player) => {
          const slide = player.closest('carousel-slide');
          if (!slide?.hasAttribute('inert') || slide.hidden || !player.getClientRects().length) return false;
          const bounds = player.getBoundingClientRect();
          return event.clientX >= bounds.left && event.clientX <= bounds.left + bounds.width
            && event.clientY >= bounds.top && event.clientY <= bounds.top + bounds.height;
        });
        if (!repeated && preview) target = preview.closest('.fit-video-wrapper');
      }
      if (!target?.closest('.fit-video-wrapper') || target.closest('a, [data-fit-media-product-link]')) return;
      const slide = target.closest('carousel-slide');
      if (!slides.includes(slide)) return;
      const player = visiblePlayer(slide);
      pendingSelection = slide;
      const changed = active !== slide;
      select(slide);
      centerSlide(slide);
      if (!player || !ready) return; // Provider players retain their own controls.
      suspended = false;
      explicitPlayback = true;
      const sound = target.closest('[data-fit-review-sound]');
      const toggle = target.closest('[data-fit-review-play], [data-ref="toggleMediaButton"], [data-ref="deferredMediaPlayButton"]');
      if (sound || toggle || changed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (sound) {
          muted = !muted;
          load(player);
        } else if (toggle && !changed) {
          userPaused = !player.querySelector('video')?.paused && !!player.querySelector('video');
          if (userPaused) pause(player);
        }
        // Selecting another clip keeps a previous explicit pause in effect.
        if (userPaused) load(player);
        else playSelected(true);
        syncControls();
      } else {
        // Native video controls continue handling seeking, play, and pause.
        allowedPlayer = player;
      }
    };
    const beginScroll = () => {
      pendingSelection = null;
      userScrolling = true;
    };
    const navigate = (index) => {
      const items = visibleSlides();
      const slide = items[loop ? ((index % items.length) + items.length) % items.length : Math.max(0, Math.min(index, items.length - 1))];
      if (!slide) return;
      pendingSelection = slide;
      suspended = false;
      explicitPlayback = true;
      select(slide);
      centerSlide(slide);
      playSelected(true);
    };
    carousel.addEventListener('shopify:block:select', (event) => {
      if (!loop) return;
      const slide = event.target.closest?.('carousel-slide');
      const index = visibleSlides().indexOf(slide);
      if (index < 0) return;
      event.stopPropagation();
      navigate(index);
    }, { signal });
    carousel.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('.carousel-control[data-action]') : null;
      if (!button || button.closest('slide-carousel') !== scroller.closest('slide-carousel')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate(visibleSlides().indexOf(active) + (button.dataset.action === 'next' ? 1 : -1));
    }, { capture: true, signal });
    scroller.addEventListener('scroll', schedule, { passive: true, signal });
    scroller.addEventListener('scrollend', () => {
      // A loop rebase also emits scrollend. Keep the gesture alive until the
      // scroll stream is quiet; otherwise momentum snaps to an earlier review.
      if (!loop) settle();
    }, { signal });
    scroller.addEventListener('wheel', (event) => {
      if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      beginScroll();
      // Keep native horizontal scrolling. The shared slider's edge-loop wheel
      // handler must not consume gestures in the independently centered row.
      event.stopImmediatePropagation();
    }, { capture: true, passive: true, signal });
    scroller.addEventListener('pointerdown', beginScroll, { passive: true, signal });
    scroller.addEventListener('keydown', (event) => {
      if (event.target !== scroller || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = visibleSlides();
      const direction = getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
        : items.indexOf(active) + (event.key === 'ArrowRight' ? direction : -direction);
      navigate(index);
    }, { capture: true, signal });
    scroller.addEventListener('click', selectVideo, { capture: true, signal });
    scroller.addEventListener('play', (event) => {
      const video = event.target;
      const player = video.closest?.('lazy-media');
      if (!players.includes(player)) return;
      if (player !== allowedPlayer || !visible || document.hidden) {
        pause(player);
        event.stopImmediatePropagation();
        return;
      }
      userPaused = false;
      pauseOthers(player);
      syncControls();
    }, { capture: true, signal });
    scroller.addEventListener('pause', (event) => {
      const video = event.target;
      if (!players.includes(video.closest?.('lazy-media'))) return;
      if (systemPauses.has(video)) systemPauses.delete(video);
      else if (active?.contains(video) && visible && inHorizontalView(video.closest('lazy-media')) && !document.hidden && !video.ended) userPaused = true;
      syncControls();
    }, { capture: true, signal });
    scroller.addEventListener('ended', syncControls, { capture: true, signal });
    scroller.addEventListener('volumechange', (event) => {
      if (active?.contains(event.target) && players.includes(event.target.closest?.('lazy-media'))) muted = event.target.muted;
      syncControls();
    }, { capture: true, signal });
    document.addEventListener('media:play', (event) => {
      const player = event.detail?.resource;
      if (players.includes(player)) {
        if (player !== allowedPlayer) {
          pause(player);
          event.stopImmediatePropagation();
        }
      } else {
        suspended = true;
        allowedPlayer = null;
        pauseOthers();
      }
    }, { capture: true, signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pauseOthers();
      else playSelected(explicitPlayback);
    }, { signal });
    const previewObserver = new IntersectionObserver((entries) => {
      previewRatio = entries.at(-1).intersectionRatio;
      visible = previewRatio > 0;
      if (visible) playSelected(explicitPlayback);
      else pauseOthers();
    }, { threshold: [0, 0.5] });
    let observedWidth = scroller.clientWidth;
    const resizeObserver = new ResizeObserver(() => {
      if (scroller.clientWidth === observedWidth) return;
      observedWidth = scroller.clientWidth;
      pauseOthers();
      sizeFrames();
      loop?.resize();
      if (active) centerSlide(active, 'instant');
      playSelected(explicitPlayback);
    });
    resizeObserver.observe(scroller);
    reviewCarousels.set(carousel, () => {
      controller.abort();
      previewObserver.disconnect();
      resizeObserver.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(settleTimer);
      pauseOthers();
      for (const toolbar of controls.values()) toolbar.remove();
      for (const [player, enabled] of nativeControls) {
        const video = player.querySelector('video');
        const templateVideo = player.querySelector('template')?.content.querySelector('video');
        if (video) video.controls = enabled;
        if (templateVideo) templateVideo.controls = enabled;
      }
      for (const player of players) {
        player.style.removeProperty('--fit-review-media-height');
        player.removeAttribute('data-fit-review-frame');
      }
      loop?.destroy();
      scroller.style.minHeight = originalMinHeight;
      delete carousel.dataset.fitVideoReviewReady;
    });

    loop = createReviewLoop(scroller, slides);
    const items = visibleSlides();
    const initial = fresh && items.length > 2
      ? items[Math.floor(items.length / 2)] : nearestSlide();
    select(initial);
    if (initial) centerSlide(initial, 'instant');
    carousel.dataset.fitVideoReviewReady = 'true';
    customElements.whenDefined('lazy-media').then(() => {
      if (signal.aborted) return;
      pauseOthers(); // Cancel each lazy-media player's independent first-visibility autoplay.
      for (const player of players) {
        nativeVideo(player).removeAttribute('autoplay');
        nativeVideo(player).muted = true;
        if (!controlsTemplate || player.closest('.fit-video-wrapper').dataset.showControls === 'false') continue;
        const toolbar = controlsTemplate.content.firstElementChild.cloneNode(true);
        player.append(toolbar);
        controls.set(player, toolbar);
        // Replace native chrome only after the accessible section controls exist.
        // Keep native controls as the no-JavaScript and teardown fallback.
        nativeControls.set(player, nativeVideo(player).controls);
        nativeVideo(player).controls = false;
        const templateVideo = player.querySelector('template')?.content.querySelector('video');
        if (templateVideo) templateVideo.controls = false;
      }
      ready = true;
      sizeFrames();
      syncControls();
      previewObserver.observe(scroller);
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeReviewCarousels(), { once: true });
} else {
  initializeReviewCarousels();
}

document.addEventListener('shopify:section:load', (event) => initializeReviewCarousels(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  for (const carousel of reviewRoots(event.target)) {
    reviewCarousels.get(carousel)?.();
    reviewCarousels.delete(carousel);
  }
});
